import { fetchFoldersChronologically as fetchFoldersChronologicallyAPI } from '../../folders.api';
import {
  getFolderDecisions,
  getFolderDecisionsAnnotations,
} from '../../page/folders.api';
import {
  FOLDERS_SET,
  FOLDER_CREATED,
  FOLDER_DELETED,
  FOLDER_RENAMED,
  FOLDER_ANNOTATION_CHANGED,
  FOLDER_DECISION_DELETED,
  FOLDER_LOAD_DECISIONS,
  FOLDER_GET_DECISIONS,
  FOLDER_ERROR_DECISIONS,
  FOLDER_LOAD_ANNOTATIONS,
  FOLDER_GET_ANNOTATIONS,
  FOLDER_ERROR_ANNOTATIONS,
} from '../action-types';
import { Folder } from '../../folders.types';
import { CreateFolderType } from '../foldersState.types';
import {
  FolderDecision,
  FolderDecisionAnnotation,
} from '../../page/folders.types';

export const loadDecisionsFromFolder = (folderKey: string) =>
  <const>{
    type: FOLDER_LOAD_DECISIONS,
    payload: { folderKey },
  };

export const errorDecisionsFromFolder = (
  folderKey: string,
  error: { response: {} },
) =>
  <const>{
    type: FOLDER_ERROR_DECISIONS,
    payload: { folderKey, error },
  };

export const getDecisionsFromFolder = (
  folderKey: string,
  decisions: FolderDecision[],
  size?: number,
) =>
  <const>{
    type: FOLDER_GET_DECISIONS,
    payload: { folderKey, decisions, size },
  };

export const fetchDecisionsFromFolder = (
  from: number,
  folderKey: string,
  size?: number,
) => {
  return async (dispatch: Function) => {
    try {
      dispatch(loadDecisionsFromFolder(folderKey));
      const decisions = await getFolderDecisions(from, folderKey, size);
      dispatch(getDecisionsFromFolder(folderKey, decisions, size));
    } catch (error) {
      dispatch(errorDecisionsFromFolder(folderKey, error));
      throw error;
    }
  };
};

export const loadAnnotationsFromFolder = (folderKey: string) =>
  <const>{
    type: FOLDER_LOAD_ANNOTATIONS,
    payload: { folderKey },
  };

export const errorAnnotationsFromFolder = (
  folderKey: string,
  error: { response: {} },
) =>
  <const>{
    type: FOLDER_ERROR_ANNOTATIONS,
    payload: { folderKey, error },
  };

export const getAnnotationsFromFolder = (
  folderKey: string,
  annotations: FolderDecisionAnnotation[],
) =>
  <const>{
    type: FOLDER_GET_ANNOTATIONS,
    payload: { folderKey, annotations },
  };

export const fetchAnnotationsFromFolder = (folderKey: string) => {
  return async (dispatch: Function) => {
    try {
      dispatch(loadAnnotationsFromFolder(folderKey));
      const annotations = await getFolderDecisionsAnnotations(folderKey);
      dispatch(getAnnotationsFromFolder(folderKey, annotations));
    } catch (error) {
      dispatch(errorAnnotationsFromFolder(folderKey, error));
      throw error;
    }
  };
};

export const setFolders = (rawFolders: Array<Folder>) => {
  const foldersOrderedByName = [...rawFolders];
  foldersOrderedByName.sort((a, b) => a.title.localeCompare(b.title));
  // folders are ordered chronologically from API call but should be ordered alphabetically on folders page
  const orderByName = foldersOrderedByName.map((folder) => folder.folderKey);

  const folders = rawFolders.reduce(
    (acc, folder) => ({ ...acc, [folder.folderKey]: folder }),
    {},
  );
  const decisionsCountByFolder = rawFolders.reduce(
    (acc, folder) => ({ ...acc, [folder.folderKey]: folder.numberOfDecisions }),
    {},
  );

  return <const>{
    type: FOLDERS_SET,
    payload: { folders, orderByName, decisionsCountByFolder },
  };
};

export const folderCreated = (folder: CreateFolderType, source: string) =>
  <const>{
    type: FOLDER_CREATED,
    payload: { folder },
    analytics: {
      type: 'Folder FolderCreated',
      data: {
        folder_key: folder.folderKey,
        source,
      },
    },
  };

export const folderDeleted = (folderKey: string) =>
  <const>{
    type: FOLDER_DELETED,
    payload: { folderKey },
    analytics: {
      type: 'Folder FolderDeleted',
      data: {
        folder_key: folderKey,
      },
    },
  };

export const decisionDeletedFromFolder = (folderKey: string, docId: string) =>
  <const>{
    type: FOLDER_DECISION_DELETED,
    payload: { folderKey, docId },
  };

export const folderRenamed = ({
  folderKey,
  title,
  source,
}: {
  folderKey: string;
  title: string;
  source: string;
}) =>
  <const>{
    type: FOLDER_RENAMED,
    payload: { folderKey, title },
    analytics: {
      type: 'Folder FolderRenamed',
      data: {
        folder_key: folderKey,
        source,
      },
    },
  };

export const folderAnnotationChanged = ({
  folderKey,
  folderAnnotation,
}: {
  folderKey: string;
  folderAnnotation: string;
}) =>
  <const>{
    type: FOLDER_ANNOTATION_CHANGED,
    payload: { folderKey, folderAnnotation },
  };

export const fetchFoldersChronologically = () => async (dispatch: Function) => {
  const folders = await fetchFoldersChronologicallyAPI();
  dispatch(setFolders(folders));
};

export type FolderActions = ReturnType<
  | typeof setFolders
  | typeof folderCreated
  | typeof folderDeleted
  | typeof decisionDeletedFromFolder
  | typeof folderRenamed
  | typeof folderAnnotationChanged
  | typeof loadDecisionsFromFolder
  | typeof errorDecisionsFromFolder
  | typeof getDecisionsFromFolder
  | typeof loadAnnotationsFromFolder
  | typeof errorAnnotationsFromFolder
  | typeof getAnnotationsFromFolder
>;
